'use client';

import { FC } from 'react';
import { Content, isFilled, asText } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import HighlightedText from '@/components/HighlightedText/HighlightedText';
import { ArrowRightIcon } from '@/assets';
import { RevealOnScroll } from '@/components/common/RevealOnScroll';

/**
 * Props for `HeroWithSteps`.
 */
export type HeroWithStepsProps =
  SliceComponentProps<Content.HeroWithStepsSlice>;

// Card gradient per index — Explorer (dark purple #13094A) then Coder (bright purple #4B0FEA)
const CARD_GRADIENTS = [
  'linear-gradient(180deg, #041118 46.68%, rgba(0,0,0,0) 100%), linear-gradient(270deg, #041118 0%, #13094A 50%, #041118 100%)',
  'linear-gradient(180deg, #041118 46.68%, rgba(0,0,0,0) 100%), linear-gradient(270deg, #041118 0%, #4B0FEA 48.39%, #041118 100%)',
];

/**
 * Component for "HeroWithSteps" Slices.
 */
const HeroWithSteps: FC<HeroWithStepsProps> = ({ slice }) => {
  const { logo, headline, subheadline, primary_cta, steps, background_image } =
    slice.primary;
  const secondary_cta = (
    slice.primary as { secondary_cta?: typeof primary_cta }
  ).secondary_cta;
  const kicker = (slice.primary as { kicker?: string }).kicker;
  const headline_line2 = (slice.primary as { headline_line2?: typeof headline })
    .headline_line2;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative mx-[30px] overflow-hidden rounded-[24px] bg-[#041118] px-4 py-[80px] md:px-[60px] lg:px-[150px]"
    >
      {isFilled.image(background_image) && (
        <PrismicNextImage
          field={background_image}
          className="absolute inset-0 size-full object-cover"
          fallbackAlt=""
        />
      )}
      <div className="absolute inset-0 bg-[rgba(4,17,24,0.7)]" />
      <div className="relative z-[1] flex flex-col items-center gap-[60px]">
        {/* ── Header ── */}
        <RevealOnScroll className="flex w-full flex-col items-center gap-[24px] text-center">
          {kicker && (
            <span className="inline-flex rounded-full border border-[#00f557] px-4 py-2 text-[12px] font-medium uppercase leading-[1.2] tracking-[0.04em] text-white md:text-[14px]">
              {kicker}
            </span>
          )}

          {isFilled.image(logo) && (
            <PrismicNextImage
              field={logo}
              className="h-[120px] w-auto object-contain"
              fallbackAlt=""
            />
          )}

          {isFilled.richText(headline) && (
            <h1 className="text-balance text-[42px] font-semibold capitalize leading-[1.2] text-white md:text-[56px]">
              <HighlightedText
                text={asText(headline)}
                highlightClassName="text-[#00f557]"
              />
              {headline_line2 && isFilled.richText(headline_line2) && (
                <>
                  <br />
                  <HighlightedText
                    text={asText(headline_line2)}
                    highlightClassName="text-[#00f557]"
                  />
                </>
              )}
            </h1>
          )}

          {isFilled.richText(subheadline) && (
            <PrismicRichText
              field={subheadline}
              components={{
                paragraph: ({ children }) => (
                  <p className="mx-auto w-full max-w-[1000px] text-pretty text-[18px] font-normal leading-[1.5] text-white">
                    {children}
                  </p>
                ),
              }}
            />
          )}

          {(isFilled.link(primary_cta) ||
            (secondary_cta && isFilled.link(secondary_cta))) && (
            <div className="flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center sm:gap-4">
              {isFilled.link(primary_cta) && (
                <PrismicNextLink field={primary_cta} className="primary-button">
                  <ArrowRightIcon className="arrow-icon" />
                  {(primary_cta as { text?: string }).text}
                </PrismicNextLink>
              )}
              {secondary_cta && isFilled.link(secondary_cta) && (
                <PrismicNextLink
                  field={secondary_cta}
                  className="secondary-button"
                >
                  <ArrowRightIcon className="arrow-icon" />
                  {(secondary_cta as { text?: string }).text}
                </PrismicNextLink>
              )}
            </div>
          )}
        </RevealOnScroll>

        {/* ── Step cards ── */}
        {steps.length > 0 && (
          <RevealOnScroll
            delay={200}
            className="flex w-full flex-col gap-[24px] md:flex-row"
          >
            {steps.map((step, index) => (
              <div
                key={index}
                className="flex flex-1 flex-col items-center gap-[24px] rounded-[24px] p-[24px] backdrop-blur-[21px] transition-transform duration-300 hover:-translate-y-2"
                style={{
                  backgroundImage:
                    CARD_GRADIENTS[index % CARD_GRADIENTS.length],
                }}
              >
                {isFilled.image(step.icon) && (
                  <PrismicNextImage
                    field={step.icon}
                    className="h-[56px] w-auto object-contain"
                    fallbackAlt=""
                  />
                )}

                <div className="flex w-full flex-col gap-[8px] text-center">
                  {isFilled.richText(step.step_title) && (
                    <PrismicRichText
                      field={step.step_title}
                      components={{
                        heading3: ({ children }) => (
                          <h3 className="text-[24px] font-semibold capitalize leading-[1.5] text-white">
                            {children}
                          </h3>
                        ),
                        paragraph: ({ children }) => (
                          <p className="text-[24px] font-semibold capitalize leading-[1.5] text-white">
                            {children}
                          </p>
                        ),
                      }}
                    />
                  )}

                  {isFilled.richText(step.step_highlight) && (
                    <PrismicRichText
                      field={step.step_highlight}
                      components={{
                        heading3: ({ children }) => (
                          <h3 className="text-[24px] font-semibold capitalize leading-[1.5] text-[#00f557]">
                            {children}
                          </h3>
                        ),
                        paragraph: ({ children }) => (
                          <p className="text-[24px] font-semibold capitalize leading-[1.5] text-[#00f557]">
                            {children}
                          </p>
                        ),
                      }}
                    />
                  )}

                  {isFilled.richText(step.step_description) && (
                    <PrismicRichText
                      field={step.step_description}
                      components={{
                        paragraph: ({ children }) => (
                          <p className="text-[16px] font-light leading-[1.5] text-white">
                            {children}
                          </p>
                        ),
                      }}
                    />
                  )}
                </div>

                {isFilled.link(step.step_cta) && (
                  <PrismicNextLink
                    field={step.step_cta}
                    className="secondary-button"
                  >
                    <ArrowRightIcon className="arrow-icon" />
                    {(step.step_cta as { text?: string }).text}
                  </PrismicNextLink>
                )}
              </div>
            ))}
          </RevealOnScroll>
        )}
      </div>
    </section>
  );
};

export default HeroWithSteps;
